import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'email-tags-input',
    templateUrl: './email-tags-input.component.html',
    styleUrls: ['./email-tags-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EmailTagsInputComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class EmailTagsInputComponent implements ControlValueAccessor {
    @Input() placeholder: string = 'Add email';
    @Input() disabled: boolean = false;

    public tags: string[] = [];
    public inputValue: string = '';
    public isInputFocused: boolean = false;

    private onChange = (value: string[]) => {};
    private onTouched = () => {};

    constructor () {}

    public onInputKeydown (event: KeyboardEvent): void {
        const key = event.key;
        const value = this.inputValue.trim();

        // Handle backspace when input is empty - remove last tag
        if (key === 'Backspace' && !this.inputValue && this.tags.length > 0) {
            this.removeTag(this.tags.length - 1);
            return;
        }

        // Handle comma, space, or Enter to add tag
        if ((key === ',' || key === ' ' || key === 'Enter') && value) {
            event.preventDefault();
            this.addTag(value);
            return;
        }
    }

    public onInputBlur (): void {
        this.isInputFocused = false;
        this.onTouched();

        // Add tag on blur if there's a valid value
        const value = this.inputValue.trim();
        if (value) {
            this.addTag(value);
        }
    }

    public onInputFocus (): void {
        this.isInputFocused = true;
    }

    public addTag (email: string): void {
        const trimmedEmail = email.trim();

        // Don't add empty tags or duplicates
        if (!trimmedEmail || this.tags.includes(trimmedEmail)) {
            this.inputValue = '';
            return;
        }

        // Validate email format
        if (!this.isValidEmail(trimmedEmail)) {
            this.inputValue = '';
            return;
        }

        this.tags.push(trimmedEmail);
        this.inputValue = '';
        this.onChange(this.tags);
    }

    public removeTag (index: number): void {
        if (index >= 0 && index < this.tags.length) {
            this.tags.splice(index, 1);
            this.onChange(this.tags);
        }
    }

    private isValidEmail (email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        // regex: ^[a-zA-Z0-9.!#$%&'*+/=?^_\`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$
        return emailRegex.test(email);
    }

    // ControlValueAccessor implementation
    public writeValue (value: string[]): void {
        this.tags = value || [];
    }

    public registerOnChange (fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched (fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState (isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
